import React, { useMemo, useState } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { MainLayout } from "../../layout/mainLayout";
import { MuiDataGrid } from "../../framework/muiDataGrid";
import { useFetch } from "../../../hooks/useFetch";
import { Base } from "../../../framework/base";
import { FormAnswerListItemDto } from "../../../models/formAnswer/formAnswerListItemDto";
import * as formAnswerService from "../../../services/formAnswerService";
import { useAppSelector } from "../../../framework/customStore";
import { FormAnswerPreviewDialog } from "./formAnswerPreviewDialog";
import { FormSummaryFilters } from "./formSummaryFilters";

export const FormSummaryView = () => {
    return (
        <MainLayout noPadding topComponent={<FormSummaryFilters />}>
            <FormSummaryViewContent />
        </MainLayout>
    );
};

const FormSummaryViewContent = () => {
    const { t } = useTranslation();
    const {
        dateRange,
        relatedObjectType,
        relatedObjectIds,
        formTemplateIds,
        answeredByEmployeeIds,
    } = useAppSelector((state) => state.formSummary);
    const [rows, setRows] = useState<FormAnswerListItemDto[]>([]);
    const [selectedAnswer, setSelectedAnswer] = useState<{
        id: string;
        formName: string;
    } | null>(null);

    const apiParams = useMemo(() => {
        const params: {
            startDate?: string;
            endDate?: string;
            relatedObjectType?: string;
            relatedObjectIds?: string[];
            formTemplateIds?: string[];
            answeredByEmployeeIds?: string[];
        } = {};
        if (dateRange[0]) params.startDate = dateRange[0];
        if (dateRange[1]) params.endDate = dateRange[1];
        if (relatedObjectType) params.relatedObjectType = relatedObjectType;
        if (relatedObjectIds.length > 0)
            params.relatedObjectIds = relatedObjectIds;
        if (formTemplateIds.length > 0)
            params.formTemplateIds = formTemplateIds;
        if (answeredByEmployeeIds.length > 0)
            params.answeredByEmployeeIds = answeredByEmployeeIds;
        return params;
    }, [
        dateRange,
        relatedObjectType,
        relatedObjectIds,
        formTemplateIds,
        answeredByEmployeeIds,
    ]);

    useFetch(
        (signal) => formAnswerService.getFormAnswers(apiParams, signal),
        { onSuccess: (response) => setRows(response.data) },
        [apiParams]
    );

    const columns: GridColDef<FormAnswerListItemDto>[] = useMemo(
        () => [
            {
                field: "formName",
                headerName: t("reporting.formSummary.formName"),
                flex: 2,
            },
            {
                field: "relatedObjectName",
                headerName: t("reporting.formSummary.target"),
                flex: 1.5,
                renderCell: ({
                    value,
                }: GridRenderCellParams<
                    FormAnswerListItemDto,
                    string | null
                >) => value ?? "-",
            },
            {
                field: "answeredAt",
                headerName: t("reporting.formSummary.answeredAt"),
                flex: 1.5,
                renderCell: ({
                    value,
                }: GridRenderCellParams<FormAnswerListItemDto, string>) =>
                    value ? Base.dayjsDateToDateTimeStr(value) : "",
            },
            {
                field: "answeredByEmployeeName",
                headerName: t("reporting.formSummary.answeredBy"),
                flex: 1.5,
            },
            {
                field: "actions",
                headerName: "",
                width: 50,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (
                    params: GridRenderCellParams<FormAnswerListItemDto>
                ) => (
                    <Tooltip title={t("settings.forms.showForm")}>
                        <IconButton
                            size="small"
                            color="primary"
                            data-testid={`form-answer-show-${params.row.formAnswerId}`}
                            onClick={() =>
                                setSelectedAnswer({
                                    id: params.row.formAnswerId,
                                    formName: params.row.formName,
                                })
                            }
                        >
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ),
            },
        ],
        [t]
    );

    return (
        <>
            <MuiDataGrid
                title={t("reporting.formSummary.tabTitle")}
                columns={columns}
                rows={rows}
                getRowId={(row: FormAnswerListItemDto) => row.formAnswerId}
                density="compact"
                disableRowSelectionOnClick
            />
            {selectedAnswer && (
                <FormAnswerPreviewDialog
                    formAnswerId={selectedAnswer.id}
                    formName={selectedAnswer.formName}
                    onClose={() => setSelectedAnswer(null)}
                />
            )}
        </>
    );
};
